import { useState } from "react";
import { Button, toast } from "@clones/ui";
import { Eye } from "lucide-react";
import { authApi } from "@/api/auth";
import { useCurrentRespondent } from "@/auth/queries";

/**
 * Phase 5 — the non-dismissable «Режим просмотра» banner shown ONLY when an
 * admin is viewing this respondent read-only (the `dca_imp` cookie; the flag
 * arrives on `/r/auth/me` as `impersonated`). Texts are §6.9 verbatim.
 *
 * «Выйти из режима» ends the viewing session server-side (clears the cookie +
 * writes `impersonation.ended`) and hard-reloads so no stale cached respondent
 * data survives the transition.
 */
export function ImpersonationBanner() {
  const { data: account } = useCurrentRespondent();
  const [exiting, setExiting] = useState(false);

  if (!account || account.impersonated !== true) return null;

  const name = account.first_name || account.email || String(account.id);

  const exit = async () => {
    if (exiting) return;
    setExiting(true);
    try {
      await authApi.impersonationExit();
    } catch {
      // The cookie clear is best-effort; a hard reload still drops the SPA's
      // cached impersonated state either way.
      toast.error("Не удалось выйти. Попробуйте ещё раз.");
      setExiting(false);
      return;
    }
    // Hard navigate (not the SPA router) so every query cache is discarded and
    // the now-cookieless app re-resolves auth from scratch.
    window.location.assign("/");
  };

  return (
    <div
      role="alert"
      className="sticky top-0 z-50 flex items-center gap-3 bg-amber-500 px-3 py-2 text-sm font-medium text-amber-950 shadow-sm sm:px-4"
    >
      <Eye className="h-4 w-4 shrink-0" aria-hidden />
      <span className="min-w-0 flex-1 truncate">
        Режим просмотра: {name} · вы видите экран респондента
      </span>
      <Button
        variant="outline"
        size="sm"
        onClick={() => void exit()}
        disabled={exiting}
        className="shrink-0 border-amber-950/30 bg-amber-100 text-amber-950 hover:bg-amber-50"
      >
        Выйти из режима
      </Button>
    </div>
  );
}
